import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { getPrograms, getPropertyList } from "./Programs.slice";
import Layout from "./Layout";
import Table from "../../components/Table/Table";
import { ProgramItem } from "../../apis/types";
import { formatUTCDate } from "../../util/string";
import Panel from "../../components/Panel";
import QuickSearch from "../../components/QuickSearch/QuickSearch";
import ComboboxAutocomplete from "../../components/Form/ComboboxAutocomplete";
import { useAppDispatch, useAppSelector } from "../../shared/redux/hooks";
import Badge from '../../components/Badge';
import CreateProgramForm from './components/CreateProgramForm';
import { CustomContentModal as FormModal } from '../../components/CustomContentModal';

const RESULTS_PER_PAGE = 20

const Programs: React.FC = () => {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const { programs, properties } = useAppSelector((state) => state.programs)
  const { permissions } = useAppSelector((state) => state.app)

  // Read filter values from URL search params
  const urlPropertyId = searchParams.get('propertyId') || null
  const urlSearch = searchParams.get('search') || null
  const page = parseInt(searchParams.get('page') || '0', 10)

  // Use local state for filters to avoid Combobox onChange loops
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(urlPropertyId)
  const [programQuickSearchTerm, setProgramQuickSearchTerm] = useState<string | null>(urlSearch)

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [listLoading, setListLoading] = useState(true)
  const listLoadGeneration = useRef(0)

  // Sync local state FROM URL when URL changes externally (back button, direct navigation)
  useEffect(() => {
    setSelectedPropertyId(urlPropertyId)
  }, [urlPropertyId])

  useEffect(() => {
    setProgramQuickSearchTerm(urlSearch)
  }, [urlSearch])

  // Sync local state TO URL
  useEffect(() => {
    const currentUrlPropertyId = searchParams.get('propertyId') || null
    const currentUrlSearch = searchParams.get('search') || null

    // Only update URL if local state differs from URL
    if (selectedPropertyId !== currentUrlPropertyId || programQuickSearchTerm !== currentUrlSearch) {
      const newParams = new URLSearchParams(searchParams)

      if (selectedPropertyId) {
        newParams.set('propertyId', selectedPropertyId)
      } else {
        newParams.delete('propertyId')
      }

      if (programQuickSearchTerm) {
        newParams.set('search', programQuickSearchTerm)
      } else {
        newParams.delete('search')
      }

      // Reset page when filters change
      newParams.delete('page')

      setSearchParams(newParams, { replace: true })
    }
  }, [selectedPropertyId, programQuickSearchTerm, searchParams, setSearchParams])

  // Check if user has permission to create programs
  const hasCreateProgramPermission = permissions?.includes('program:manager:create') ?? false

  // Memoize property options to prevent re-render issues
  const propertyOptions = useMemo(
    () => properties.map((property) => ({
      id: property.id,
      name: property.nameLocation,
    })),
    [properties]
  )

  useEffect(() => {
    void dispatch(getPropertyList())
  }, [dispatch])

  const loadPrograms = useCallback(() => {
    const generation = ++listLoadGeneration.current
    setListLoading(true)
    void dispatch(
      getPrograms({
        page,
        resultsPerPage: RESULTS_PER_PAGE,
        programName: programQuickSearchTerm || undefined,
        propertyId: selectedPropertyId || undefined,
      })
    ).finally(() => {
      if (generation === listLoadGeneration.current) {
        setListLoading(false)
      }
    })
  }, [dispatch, page, programQuickSearchTerm, selectedPropertyId])

  useEffect(() => {
    loadPrograms()
  }, [loadPrograms])

  const handleQuickSearchChange = useCallback((value: string): void => {
    setProgramQuickSearchTerm(value || null)
  }, [])

  const handlePropertyChange = useCallback((newValue: string | null): void => {
    setSelectedPropertyId(newValue || null)
  }, [])

  const handlePaginatorClick = useCallback((newPage: number): void => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      if (newPage === 0) {
        newParams.delete('page')
      } else {
        newParams.set('page', newPage.toString())
      }
      return newParams
    })
  }, [setSearchParams])

  const handleCreateSuccess = (): void => {
    loadPrograms()
  }

  return (
    <Layout title="Programs">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Programs</h2>
        {hasCreateProgramPermission && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Create New
          </button>
        )}
      </div>
      <Panel header="Filters" className="mb-5">
        <div className="grid grid-auto-flow grid-cols-3 gap-3 mb-2">
          <div>
            <label htmlFor="propertyNames" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Property name
            </label>
            {properties !== undefined && (
              <ComboboxAutocomplete
                labelPlural="Properties"
                value={selectedPropertyId}
                handleChange={handlePropertyChange}
                data={propertyOptions}
              />
            )}
          </div>
          <QuickSearch title="Program name" initialValue={programQuickSearchTerm ?? undefined} handleChange={handleQuickSearchChange} />
        </div>
      </Panel>
      <Table<ProgramItem>
          isLoading={listLoading}
          colConfig={[
            {
              label: 'Property Name',
              render: (rec) => rec.propertyName,
            },
            {
              label: 'Program Name',
              render: (rec) => rec.name,
            },
            {
              label: 'Utility',
              render: (rec) => rec.utility,
            },
            {
              label: 'Start Date',
              render: (rec) => (rec.startDate != null ? formatUTCDate(new Date(rec.startDate)) : ''),
            },
            // Commented out for now since no programs have an end date in production
            // {
            //   label: 'End Date',
            //   render: (rec) => (rec.endDate != null ? formatUTCDate(new Date(rec.endDate)) : ''),
            // },
            {
              label: 'PTO Date',
              render: (rec) => formatUTCDate(new Date(rec.ptoDate)),
            },
            {
              label: 'VNEM Transition Start Date',
              render: (rec) => formatUTCDate(new Date(rec.billingOnDate)),
            },
            {
              label: 'VNEM Billing Active',
              render: (rec) => <Badge status={rec.billingOn ? 'YES' : 'NO'} className="justify-center" />,
            },
            {
              label: 'Allocation Percentage',
              render: (rec) => rec.totalVNEMAllocationPercentage != null && `${rec.totalVNEMAllocationPercentage}%`,
            },
          ]}
          data={
            programs ?? {
              results: [],
              total: 0,
              page: 0,
              resultsPerPage: RESULTS_PER_PAGE,
            }
          }
          getRowHref={(rec) => `/program/programs/${rec.id}`}
          handlePaginatorClick={handlePaginatorClick}
        />

      {hasCreateProgramPermission && (
        <FormModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
        >
          <CreateProgramForm
            closeFunction={() => setShowCreateModal(false)}
            onSuccess={handleCreateSuccess}
            properties={properties}
          />
        </FormModal>
      )}
    </Layout>
  )
}

export default Programs;
